/**
 * PII detection utility.
 *
 * Checks user input for patterns that may indicate
 * personally identifiable information (SSN, credit card,
 * email, phone, MBI).
 */

const piiPatterns = {
  ssn: /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/,
  creditCard: /\b(?:\d[ -]*?){13,19}\b/,
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/i,
  phone: /\b(?:\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/,
  mbi: /(?=[A-HJ-NP-RT-Y0-9-]*\d)(?:[A-HJ-NP-RT-Y0-9]{4}-[A-HJ-NP-RT-Y0-9]{3}-[A-HJ-NP-RT-Y0-9]{4}|[A-HJ-NP-RT-Y0-9]{11})/,
} as const;

const messages: Record<string, { en: string; es: string }> = {
  ssn: {
    en: 'Please do not enter a Social Security number.',
    es: 'No ingrese un número de Seguro Social.',
  },
  creditCard: {
    en: 'Please do not enter a credit card number.',
    es: 'No ingrese un número de tarjeta de crédito.',
  },
  email: {
    en: 'Please do not enter an email address.',
    es: 'No ingrese una dirección de correo electrónico.',
  },
  phone: {
    en: 'Please do not enter a phone number.',
    es: 'No ingrese un número de teléfono.',
  },
  mbi: {
    en: 'Please do not enter a Medicare Beneficiary Identifier.',
    es: 'No ingrese un identificador de beneficiario de Medicare.',
  },
};

const generalMessage = {
  en: 'Your search seems to include personal information like your Social Security number, Medicare number, or phone number.\n\nTo protect your privacy, we don\'t allow searches with personal information. Try searching for what you need without including your personal information.',
  es: 'Su búsqueda parece incluir información personal como su número de Seguro Social, número de Medicare o número de teléfono.\n\nPara proteger su privacidad, no permitimos búsquedas con información personal. Intente buscar lo que necesita sin incluir su información personal.',
};

export interface PiiCheckResult {
  hasPII: boolean;
  message: string;
}

export function checkForPII(
  input: string,
  lang: 'en' | 'es' = 'en',
  specific: boolean = false
): PiiCheckResult {
  const uppercaseInput = input.toUpperCase();

  for (const [piiType, pattern] of Object.entries(piiPatterns)) {
    if (pattern.test(uppercaseInput)) {
      return {
        hasPII: true,
        message: specific ? messages[piiType][lang] : generalMessage[lang],
      };
    }
  }

  return { hasPII: false, message: '' };
}
